import { handleCompleteSupportRequest } from '@/entities/support';
import { useLoadingStore } from '@/shared/model';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { t } from 'ttag';

/**
 * Drives the public complete-support-request screen, turning the route id into the confirmation message it shows. On mount it completes the ticket and stores the returned message, substituting a generic try-again text when the request fails or reports 'Failed to fetch', clears the global page loading text and sets the document title.
 *
 * @returns Object with the confirmation message and the loaded flag.
 */
export const useCompleteSupportRequest = () => {
  const params = useParams();

  /**
   * Page-level loading action bound for the complete-support-request screen. Selected from the loading store's loading-text setter, so invoking it writes the given text straight into global loading state.
   *
   * @param loadingText - Text to display in the global page loader.
   * @returns Nothing.
   */
  const loadingPageAction = useLoadingStore((s) => s.setLoadingText);

  const [message, setMessage] = useState('');
  const [loaded, setLoaded] = useState(false);

  // *: Ticket is not exist: Something went wrong happening to this request. Please try again later.
  // *: Ticket has already been completed: This request has already been completed. Please move on to the next one.

  /**
   * Completes the support ticket identified by the route id and stores the resulting message for display. On failure it logs the error and falls back to a generic try-again message when the error text is missing or reads 'Failed to fetch'; both paths mark the screen as loaded.
   *
   * @param id - Support ticket id taken from the route parameters.
   * @returns Promise resolving once the message and loaded state have been set.
   */
  const fetchData = async (id: any) => {
    try {
      const { message } = await handleCompleteSupportRequest({ id });
      setMessage(message ?? '');
      setLoaded(true);
    } catch (error) {
      console.error('Failed to complete your request: ', error);
      let message = error instanceof Error ? error.message : '';
      if (!message || message.trim() === 'Failed to fetch') {
        message = t`Something wrong happened to this request. Please try again later.`;
      }
      setMessage(message);
      setLoaded(true);
    }
  };

  useEffect(() => {
    const { id } = params;
    fetchData(id);
  }, []);

  useEffect(() => {
    loadingPageAction();
  }, []);

  useEffect(() => {
    document.title = t`Biocare Diagnostics - Complete Request`;
  }, []);

  return { message, loaded };
};
